import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Label } from '@/components/ui/label';
import { useCurrentUser, useEffectiveClinics } from '@/hooks/use-current-user';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClinicScopeHeader } from '../ClinicScopeHeader';
import { PdfLayoutDesigner } from './PdfLayoutDesigner';
export { PREVIEW_DEBOUNCE_MS } from './PdfLayoutDesigner';
/**
 * Settings → Reporting → Report design: the clinic mount of the shared
 * PdfLayoutDesigner. Owns the clinic picker and the Clinic Admin write gate;
 * the designer itself (layout switcher, controls, live preview) is
 * owner-parameterized and shared with the TC group mount.
 */
export function ReportDesignSettings() {
    const { t } = useTranslation('settings');
    const { currentUser, isSuperAdmin } = useCurrentUser();
    // Same clinic picker + strict gate as the branding controls on the General
    // tab: editing requires the literal Clinic Admin role in that clinic or
    // Super Admin globally (mirrors the backend PUT gate). Everyone can look.
    const { clinics } = useEffectiveClinics();
    const roleNameByClinicId = useMemo(() => {
        const m = new Map();
        for (const cr of currentUser?.clinicRoles ?? [])
            m.set(cr.clinicId, cr.roleName);
        return m;
    }, [currentUser]);
    const canManageDesign = (clinicId) => isSuperAdmin || roleNameByClinicId.get(clinicId) === 'Clinic Admin';
    const defaultClinicId = useMemo(() => {
        const firstAdminable = clinics.find((c) => canManageDesign(c.clinicId));
        return firstAdminable?.clinicId ?? clinics[0]?.clinicId ?? null;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- canManageDesign closes over isSuperAdmin and roleNameByClinicId
    }, [clinics, isSuperAdmin, roleNameByClinicId]);
    const [selectedClinicId, setSelectedClinicId] = useState(defaultClinicId);
    if (selectedClinicId === null && defaultClinicId !== null) {
        setSelectedClinicId(defaultClinicId);
    }
    const selectedClinic = clinics.find((c) => c.clinicId === selectedClinicId) ?? clinics[0] ?? null;
    // Unsaved-changes guard for clinic switching lives here (the designer owns
    // the dirty flag, reported up via onDirtyChange).
    const dirtyRef = useRef(false);
    const handleClinicSwitch = (clinicId) => {
        if (dirtyRef.current && !window.confirm(t('reporting.design.switchUnsaved'))) {
            return;
        }
        dirtyRef.current = false;
        setSelectedClinicId(clinicId);
    };
    return (_jsxs("div", { className: "space-y-4 pt-4", children: [_jsxs(ClinicScopeHeader, { clinics: clinics, value: selectedClinic?.clinicId, onValueChange: handleClinicSwitch, ariaLabel: t('reporting.clinicPicker'), showPicker: clinics.length > 1, children: [_jsx(Label, { children: t('reporting.design.title') }), _jsx("p", { className: "text-muted-foreground text-xs", children: t('reporting.design.description') })] }), !selectedClinic && (_jsx("p", { className: "text-muted-foreground text-sm", children: t('reporting.design.noClinic') })), selectedClinic && (
            // Key on the clinic so the designer's layout selection and dirty state
            // reset when the picker switches clinics.
            _jsx(PdfLayoutDesigner, { kind: "clinic", ownerId: selectedClinic.clinicId, ownerLabel: selectedClinic.clinicName, canEdit: canManageDesign(selectedClinic.clinicId), onDirtyChange: (dirty) => {
                    dirtyRef.current = dirty;
                } }, selectedClinic.clinicId))] }));
}
